// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Shared types for the Hands-Free frontend components.
// Mirrors the hf_review_items schema and the hands-free-commit request contract.
//
// As of T-205 the pipeline supports multiple workflows (runoff_log, yield_batch,
// diary_entry), so proposed_data / field_overrides are workflow-shaped. The
// ReviewCard renders fields from a per-workflow descriptor (see workflowFields.ts).

// ── measurement_values item (runoff_log; per spec §6.2 / ADR-007) ────────────

export interface MeasurementValue {
  type: "volume" | "ph" | "ec" | string;
  value: number;
  unit: string | null;
}

// ── proposed_data shapes ──────────────────────────────────────────────────────

export interface RunoffProposedData {
  zone_id: string | null;
  user_id: string;
  facility_id: string;
  log_date: string;
  volume: number | null;
  ph: number | null;
  ec: number | null;
  measurement_values: MeasurementValue[];
  notes: string | null;
}

export interface YieldProposedData {
  batch_id: string | null;
  harvest_date: string;
  room_id: string | null;
  zone_id: string | null;
  strain_id: string | null;
  strain_name: string | null;
  wet_weight: number | null;
  wet_weight_method: "batch" | "single_plant" | "average" | null;
  plant_count: number | null;
  dry_weight_a: number | null;
  notes: string | null;
  user_id: string;
  facility_id: string;
}

export interface DiaryProposedData {
  crop_id: string | null;
  entry_date: string;
  notes: string | null;
  mood: "great" | "good" | "neutral" | "poor" | "bad" | null;
  tags: string[];
  created_by: string;
  facility_id: string;
}

/** Generic proposed_data — the ReviewCard reads it via the workflow descriptor. */
export type ProposedData = Record<string, unknown> & {
  facility_id: string;
  measurement_values?: MeasurementValue[];
};

// ── resolution_summary (from hands-free-resolve) ─────────────────────────────

export interface ResolutionEntry {
  status: "resolved" | "ambiguous" | "not_found";
  raw_text: string;
  resolved_id: string | null;
  candidates: Array<{ id: string; label: string }> | null;
}

/** Per-entity outcomes vary by workflow; the UI only relies on `warnings`. */
export type ResolutionSummary = {
  warnings?: string[];
} & Record<string, unknown>;

// ── hf_review_items row ───────────────────────────────────────────────────────

export type ReviewItemStatus =
  | "pending_review"
  | "review_required"
  | "approved"
  | "rejected"
  | "committed"
  | "error";

export interface HfReviewItem {
  id: string;
  workflow_type: string;
  status: ReviewItemStatus;
  proposed_data: ProposedData;
  resolution_summary: ResolutionSummary | null;
  committed_record_id: string | null;
  committed_table: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  facility_id: string;
  transcript_id: string;
}

// ── Field overrides — workflow-shaped; filtered server-side to allowed keys ───

export type FieldOverrides = Record<string, unknown>;

// ── Commit response ───────────────────────────────────────────────────────────

export interface CommitSuccessResponse {
  committed_record_id: string;
  committed_table: string;
  review_item_id: string;
  status: "committed";
}

export interface CommitErrorResponse {
  error: string;
  detail?: string;
  committed_record_id?: string;
}

// ── Pipeline result (from hands-free-transcribe / text / vision entry) ────────

export interface PipelineResult {
  transcript_id: string;
  parse_result_id?: string;
  review_item_id?: string;
  workflow_type?: string;
  status: string;
  raw_transcript?: string;
  proposed_data?: ProposedData;
  resolution_summary?: ResolutionSummary;
}

/** Workflow types the UI knows how to render + record. */
export const WORKFLOW_TYPES = [
  "runoff_log",
  "yield_batch",
  "diary_entry",
  "crop_note",
  "application_log",
] as const;
export type WorkflowType = typeof WORKFLOW_TYPES[number];

export const WORKFLOW_LABELS: Record<string, string> = {
  runoff_log: "Runoff log",
  yield_batch: "Yield / harvest",
  diary_entry: "Diary note",
  crop_note: "Crop note",
  application_log: "Application log",
};

/** Page-context pins forwarded to ingest edge functions. */
export interface HandsFreeContext {
  crop_id?: string;
  room_id?: string;
  zone_id?: string;
  workflow?: WorkflowType;
}
